/**
 * Review Highlights utilities.
 *
 * Extract key themes and sentiment from listing reviews.
 * Shows top keywords (e.g., "clean", "friendly", "convenient")
 * with mention counts. Client-side only — no server required.
 *
 * Algorithm: tokenize review text, remove stop words, count top words.
 */

// Common stop words to exclude
export const STOP_WORDS = new Set([
  'a', 'an', 'the', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for',
  'of', 'with', 'by', 'is', 'was', 'are', 'were', 'be', 'been', 'being',
  'have', 'has', 'had', 'do', 'does', 'did', 'will', 'would', 'could',
  'should', 'may', 'might', 'shall', 'can', 'this', 'that', 'these', 'those',
  'i', 'me', 'my', 'we', 'our', 'you', 'your', 'he', 'she', 'it', 'they',
  'their', 'them', 'it', 'so', 'as', 'from', 'up', 'out', 'if', 'about',
  'very', 'just', 'also', 'really', 'quite', 'too', 'more', 'most', 'not',
]);

const MIN_WORD_LENGTH = 4;
const MAX_HIGHLIGHTS = 5;

/**
 * Tokenize text into words.
 * @param {string} text
 * @returns {string[]}
 */
export const tokenize = (text) => {
  if (!text) return [];
  return text.toLowerCase()
    .replace(/[^a-z\s]/g, ' ')
    .split(/\s+/)
    .filter(w => w.length >= MIN_WORD_LENGTH && !STOP_WORDS.has(w));
};

/**
 * Count word frequencies.
 * @param {string[]} words
 * @returns {Map<string, number>}
 */
export const countWords = (words) => {
  const counts = new Map();
  for (const word of words) {
    counts.set(word, (counts.get(word) || 0) + 1);
  }
  return counts;
};

/**
 * Extract top highlights from reviews.
 * @param {Object[]} reviews — [{ attributes: { content: string } }]
 * @param {number} [maxHighlights]
 * @returns {{ word: string, count: number }[]}
 */
export const extractHighlights = (reviews, maxHighlights = MAX_HIGHLIGHTS) => {
  if (!Array.isArray(reviews) || !reviews.length) return [];

  const allWords = reviews.flatMap(r => tokenize(r?.attributes?.content || ''));
  const counts = countWords(allWords);

  return [...counts.entries()]
    .filter(([, count]) => count >= 2) // only words mentioned 2+ times
    .sort((a, b) => b[1] - a[1])
    .slice(0, maxHighlights)
    .map(([word, count]) => ({ word, count }));
};

/**
 * Classify word sentiment (simple positive/negative detection).
 * @param {string} word
 * @returns {'positive'|'negative'|'neutral'}
 */
const POSITIVE = new Set(['great', 'good', 'excellent', 'clean', 'friendly', 'helpful', 'amazing', 'wonderful', 'perfect', 'comfortable', 'beautiful', 'nice', 'best', 'lovely', 'awesome', 'fantastic']);
const NEGATIVE = new Set(['bad', 'dirty', 'noisy', 'cold', 'broken', 'small', 'dark', 'slow', 'poor', 'terrible', 'awful', 'worst', 'late', 'rude', 'wrong']);

export const classifySentiment = (word) => {
  const w = word.toLowerCase();
  if (POSITIVE.has(w)) return 'positive';
  if (NEGATIVE.has(w)) return 'negative';
  return 'neutral';
};
